import { DataDisplay, Text } from "@certn/cascadia-react";
import { css } from "@emotion/css";
import dayjs from "dayjs";
import { t } from "i18next";
import _ from "lodash";

import { getCurrencySymbol } from "./utils";
import { InvoiceProps } from "../../../../src/schemas-and-types";

export type AnonymizedUsagesTableProps = {
  invoiceData: InvoiceProps["invoiceData"];
};

function buildUsagesRows(
  usages: InvoiceProps["invoiceData"]["detailed_usages"],
  currencySymbol: string
) {
  return usages.map((usage) => ({
    key: usage.usage_id,
    group: usage.customer_group_name,
    caseId: usage.report_link ? (
      <a href={usage.report_link}>{usage.case_id}</a>
    ) : (
      usage.case_id
    ),
    billableDate: dayjs.utc(usage.billable_date).format("YYYY-MM-DD"),
    caseClosedDate: dayjs.utc(usage.case_closed_date).format("YYYY-MM-DD"),
    item: usage.name,
    description: usage.description ? usage.description : "",
    status: usage.status,
    price: usage.price ? `${currencySymbol}${usage.price}` : "",
    tax: usage.tax ? `${currencySymbol}${usage.tax}` : "",
    total: usage.total ? `${currencySymbol}${usage.total}` : "",
  }));
}

function GroupUsagesTable(props: {
  invoiceData: InvoiceProps["invoiceData"];
  usages: InvoiceProps["invoiceData"]["detailed_usages"];
  segmentKey: string;
}) {
  const { invoiceData } = props;
  const currencySymbol = getCurrencySymbol(invoiceData.currency);
  const columns = [
    { key: "caseId", name: t("Case ID"), isRowHeader: true },
    { key: "billableDate", name: t("Billable") },
    { key: "caseClosedDate", name: t("Case Closed") },
    { key: "item", name: t("Item") },
    { key: "description", name: t("Description") },
    { key: "status", name: t("Status") },
    {
      key: "price",
      name: t("Price").concat(`(${currencySymbol}${invoiceData.currency})`),
    },
    {
      key: "tax",
      name: t("Tax").concat(`(${currencySymbol}${invoiceData.currency})`),
    },
    {
      key: "total",
      name: t("Total").concat(`(${currencySymbol}${invoiceData.currency})`),
    },
  ];
  const usagesRows = buildUsagesRows(props.usages, currencySymbol);

  return (
    <DataDisplay
      className={styles.table}
      renderTopMargin={false}
      stickyHeader
      columns={columns}
      rows={[
        ...usagesRows,
        {
          key: "totals",
          group: "",
          caseId: "",
          billableDate: "",
          caseClosedDate: "",
          item: "",
          description: "",
          status: "",
          price: "",
          tax: (
            <Text style="global/button-small" className={styles.summary}>
              {t("Group Total:")}
            </Text>
          ),
          total: `${currencySymbol}${invoiceData.segments[props.segmentKey].total}`,
        },
      ]}
      testId="anonymized-usages-table"
      aria-label="Anonymized Usages Table"
    />
  );
}

export const AnonymizedUsagesTable = (props: AnonymizedUsagesTableProps) => {
  const { invoiceData } = props;

  // Group usages by customer name
  const groupedUsages: Record<
    string,
    InvoiceProps["invoiceData"]["detailed_usages"]
  > = _.groupBy(invoiceData.detailed_usages, "segment");

  if (invoiceData.detailed_usages.length > 100) {
    return (
      <>
        <div>
          <Text style="global/heading-large">
            {t(
              "File size limit exceeded, please go to Client Portal page to download full list of line item details."
            )}
          </Text>
        </div>
      </>
    );
  } else {
    return (
      <>
        {Object.entries(groupedUsages).map(([key, usages]) => (
          <div key={key}>
            <Text style="global/heading-large">
              {invoiceData.segments[key].customer_group_name}
            </Text>
            <GroupUsagesTable
              key={key}
              invoiceData={invoiceData}
              usages={usages}
              segmentKey={key}
            />
            <br />
            <br />
          </div>
        ))}
      </>
    );
  }
};

const styles = {
  table: css({
    // Size of things
    "& td, & th": {
      fontSize: 11,
      padding: "4px 8px",
      lineHeight: 1,
    },
    // Horizontal alignment of header and data cells
    "& th[data-key='price'], & td[data-key$='price']": {
      textAlign: "right",
    },
    "& th[data-key='tax'], & td[data-key$='tax']": {
      textAlign: "right",
    },
    "& th[data-key='total'], & td[data-key$='total']": {
      textAlign: "right",
    },
    "& tr[data-key='totals']": {
      borderBottom: "none",
    },
  }),
  summary: css({
    fontSize: 11,
  }),
};
